import * as React from "react";

import { Input } from "./input";

/**
 * Group the integer part in threes so large sums stay readable while typing:
 * "1000000"    -> "1 000 000"
 * "1000000.5"  -> "1 000 000.5"
 *
 * A space (not a comma) is used as the separator to match local convention and
 * to keep the decimal point unambiguous.
 */
export function formatAmount(raw) {
  if (raw === "" || raw === null || raw === undefined) return "";
  const s = String(raw);
  const negative = s.startsWith("-");
  const body = negative ? s.slice(1) : s;
  const [intPart = "", decPart] = body.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const out = decPart !== undefined ? `${grouped}.${decPart}` : grouped;
  return negative ? `-${out}` : out;
}

/**
 * Strip the grouping back out so callers keep receiving a plain numeric string
 * that `parseFloat` understands. Also tolerates a comma typed as the decimal
 * mark and collapses any extra dots.
 */
export function parseAmount(display) {
  if (display === "" || display === null || display === undefined) return "";
  let cleaned = String(display).replace(/,/g, ".").replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "");
  }
  return cleaned;
}

/**
 * Text input that shows thousand separators as you type but reports the raw
 * numeric string to `onChange`, so existing `parseFloat(value)` call sites are
 * unaffected.
 *
 * `type="text"` (not `number`) because browsers reject the separator characters
 * in a number field; `inputMode="decimal"` still brings up the numeric keypad
 * on mobile.
 */
const AmountInput = React.forwardRef(({ value, onChange, ...props }, ref) => {
  const innerRef = React.useRef(null);
  const caretRef = React.useRef(null);

  React.useImperativeHandle(ref, () => innerRef.current);

  // Re-place the caret after formatting shifts the character offsets. The
  // anchor is "how many value characters precede the caret", which grouping
  // never changes.
  React.useLayoutEffect(() => {
    const node = innerRef.current;
    const target = caretRef.current;
    if (!node || target === null) return;
    caretRef.current = null;

    const formatted = node.value;
    let seen = 0;
    let pos = 0;
    while (pos < formatted.length && seen < target) {
      if (/[\d.]/.test(formatted[pos])) seen += 1;
      pos += 1;
    }
    node.setSelectionRange(pos, pos);
  });

  const handleChange = (event) => {
    const el = event.target;
    const caret = el.selectionStart ?? el.value.length;
    caretRef.current = el.value.slice(0, caret).replace(/[^\d.]/g, "").length;
    onChange?.(parseAmount(el.value));
  };

  return (
    <Input
      ref={innerRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={formatAmount(value)}
      onChange={handleChange}
      {...props}
    />
  );
});
AmountInput.displayName = "AmountInput";

export { AmountInput };
